import { Fragment, useState } from 'react';
import { Alert, Button, CircularProgress, Snackbar, SelectChangeEvent, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import { css } from '@emotion/react';

import { ILazyDropdownOption } from 'components/lazyDropdown/LazyDropdown';
import { useGetCompositeProfileInfoByQueue, useSaveWellData } from './api/useCompositeProfileController';
import { getApiErrorMessage } from '../../utils/getApiErrorMessage';
import { CompositeProfileType } from './models/CompositeProfileType';
import { CompositeProfileTypeSelect } from './components/CompositeProfileTypeSelect';
import { WellLazyDropdown } from './components/WellLazyDropdown';
import { actionButtonSx } from '../../utils/queueMeta';

const expandedContentStyle = css({
    '& .loading-wrapper': {
        display: 'flex',
        justifyContent: 'center',
        padding: 8,
    },
    '& .error-wrapper': {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 6,
        backgroundColor: '#ffebee',
        border: '1px solid #ef9a9a',
    },
    '& .info-wrapper': {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 6,
        backgroundColor: '#f0f4ff',
        border: '1px solid #c5cae9',
        minWidth: 600,
        '& .info-row': {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
        },
        '& .info-fields-row': {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            '& .well-dropdown': {
                flex: 3,
            },
            '& .type-dropdown': {
                flex: 2,
            },
        },
    },
});

interface IQueueCompositeProfileItemActionsProps {
    entityKey: string;
    queueToken: string;
    isExpanded: boolean;
}

export function QueueCompositeProfileItemActions({ entityKey, queueToken, isExpanded }: IQueueCompositeProfileItemActionsProps) {
    const { data, isLoading, isError } = useGetCompositeProfileInfoByQueue(queueToken, isExpanded);
    const saveWellData = useSaveWellData();

    const [selectedType, setSelectedType] = useState<CompositeProfileType | undefined>(undefined);
    const [selectedWell, setSelectedWell] = useState<ILazyDropdownOption<string> | null>(null);
    const [snackbar, setSnackbar] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

    const currentType = selectedType ?? data?.CompositeProfileType;
    const currentWellToken = selectedWell?.value ?? data?.WellToken;

    const initialWell: ILazyDropdownOption<string> | null = (
        data?.WellToken && data?.WellName
            ? { value: data.WellToken, label: data.WellName }
            : null
    );

    const wellValue = selectedWell ?? initialWell;

    function handleTypeChange(event: SelectChangeEvent<CompositeProfileType>) {
        setSelectedType(event.target.value as CompositeProfileType);
    }

    function handleWellChange(option: ILazyDropdownOption<string> | null) {
        setSelectedWell(option);
    }

    function handleOpenViewer() {
        window.open(`/Well/Viewer?token=${entityKey}`, '_blank');
    }

    function handleSave() {
        if (!currentWellToken || currentType === undefined) {
            return;
        }

        saveWellData.mutate(
            {
                compositeProfileToken: entityKey,
                wellToken: currentWellToken,
                type: currentType,
            },
            {
                onSuccess: () => {
                    setSnackbar({ severity: 'success', message: 'Well data saved successfully.' });
                },
                onError: (error) => {
                    setSnackbar({ severity: 'error', message: getApiErrorMessage(error) });
                },
            }
        );
    }

    return (
        <Fragment>
            <Button size='small' variant='outlined' startIcon={<ViewInArOutlinedIcon sx={{ fontSize: 16 }} />} sx={actionButtonSx} onClick={handleOpenViewer}>
                Abrir Viewer
            </Button>
            <Button size='small' variant='outlined' startIcon={<MapOutlinedIcon sx={{ fontSize: 16 }} />} sx={actionButtonSx}>
                Mostrar no Mapa
            </Button>
            {isExpanded && (
                <div css={expandedContentStyle}>
                    {isLoading && (
                        <div className='loading-wrapper'>
                            <CircularProgress size={18} />
                        </div>
                    )}
                    {isError && !isLoading && (
                        <div className='error-wrapper'>
                            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#c62828', mt: '1px', flexShrink: 0 }} />
                            <Typography variant='caption' sx={{ color: '#c62828', fontWeight: 600 }}>
                                Failed to load composite profile information.
                            </Typography>
                        </div>
                    )}
                    {!isLoading && !isError && data && (
                        <div className='info-wrapper'>
                            <div className='info-fields-row'>
                                <WellLazyDropdown
                                    className='well-dropdown'
                                    value={wellValue}
                                    onChange={handleWellChange}
                                />
                                <CompositeProfileTypeSelect
                                    className='type-dropdown'
                                    value={currentType ?? CompositeProfileType.CompositeProfile}
                                    onChange={handleTypeChange}
                                    size='small'
                                />
                                <Button
                                    size='small'
                                    variant='contained'
                                    startIcon={
                                        saveWellData.isLoading
                                            ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                            : <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                                    }
                                    disabled={saveWellData.isLoading || !currentWellToken}
                                    onClick={handleSave}
                                    sx={{ ...actionButtonSx, backgroundColor: '#2e7d32', color: '#fff', borderColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20', borderColor: '#1b5e20' } }}
                                >
                                    Salvar
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <Snackbar
                open={!!snackbar}
                autoHideDuration={5000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert variant='filled' severity={snackbar.severity} onClose={() => setSnackbar(null)}>
                        {snackbar.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Fragment>
    );
}
